import { t, useLanguage } from "@/lib/i18n";
import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function PasswordReset() {
  useLanguage();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [requested, setRequested] = useState(false);
  const [done, setDone] = useState(false);

  const perform = async (action) => {
    setBusy(true); setMessage(""); setError("");
    try { await action(); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || "Please try again."); }
    finally { setBusy(false); }
  };

  const requestReset = (event) => {
    event.preventDefault();
    perform(async () => {
      const { data } = await api.post("/auth/password-reset/request", { email: email.trim().toLowerCase() });
      setRequested(true); setMessage(data.message);
    });
  };

  const submitReset = (event) => {
    event.preventDefault();
    setError("");
    if (password !== confirmPassword) { setError("Passwords do not match."); return; }
    if (password.length < 8 || new TextEncoder().encode(password).length > 72) {
      setError("Use a password of at least 8 characters and at most 72 bytes."); return;
    }
    perform(async () => {
      const { data } = await api.post("/auth/password-reset/confirm", { token, password });
      setMessage(data.message); setDone(true); setPassword(""); setConfirmPassword("");
    });
  };

  return <main className="min-h-screen flex items-center justify-center bg-[#FAF7F2] p-5">
    <section className="w-full max-w-md bg-white border rounded-xl p-6 space-y-5">
      <div>
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-amber-800/80">{t("Account recovery")}</p>
        <h1 className="font-serif text-3xl mt-1">{t("Reset password")}</h1>
      </div>
      <p className="text-sm text-stone-600">{t("Enter your account email. If an account matches, we’ll send a reset link. Links expire after 30 minutes.")}</p>
      {message && <p role="status" className="text-sm text-green-800">{t(message)}</p>}
      {error && <p role="alert" className="text-sm text-red-700">{t(error)}</p>}
      {!done && <>
        {!token && <form onSubmit={requestReset} className="space-y-4">
          <div className="space-y-2"><Label htmlFor="reset-email">{t("Email")}</Label>
            <Input id="reset-email" type="email" autoComplete="email" required value={email} onChange={(event) => setEmail(event.target.value)} disabled={busy} /></div>
          <Button type="submit" disabled={busy || !email.trim()} className="w-full">{busy ? t("Sending…") : t(requested ? "Send another reset link" : "Send reset link")}</Button>
        </form>}
        {token && <form onSubmit={submitReset} className="space-y-4">
          <div className="space-y-2"><Label htmlFor="new-password">{t("New password")}</Label>
            <Input id="new-password" type="password" autoComplete="new-password" minLength={8} maxLength={72} required value={password} onChange={(event) => setPassword(event.target.value)} disabled={busy} /></div>
          <div className="space-y-2"><Label htmlFor="confirm-new-password">{t("Confirm new password")}</Label>
            <Input id="confirm-new-password" type="password" autoComplete="new-password" required value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} disabled={busy} /></div>
          <Button type="submit" disabled={busy} className="w-full">{busy ? t("Resetting…") : t("Reset password")}</Button>
        </form>}
      </>}
      {done && <Link className="block text-center text-amber-800 underline" to="/login">{t("Sign in with your new password")}</Link>}
      {!done && <Link className="block text-center text-sm text-amber-800 underline" to="/login">{t("Back to sign in")}</Link>}
    </section>
  </main>;
}
